import { Input, InputProps } from "@nextui-org/react"
import { ko } from "date-fns/locale"
import dayjs from "dayjs"
import React, { forwardRef, useState } from "react"
import DatePicker from "react-datepicker"
import {
  Middleware,
  shift as shiftMiddleware,
  offset as offsetMiddleware,
  flip,
} from "@floating-ui/react"

import CalendarIcon from "@/assets/icons/calendar.svg"

import DatePickerHeader from "./DatePickerHeader"

interface Props {
  date: string
  name?: string
  size?: "sm" | "md" | "lg"
  minDate?: string
  maxDate?: string
  isInvalid?: boolean
  inputRef?: React.Ref<HTMLInputElement>
  errorMessage?: string
  validation?: object
  openToDate?: Date
  placeholder?: string
  description?: string
  isClearable?: boolean
  offset?: [x: number, y: number]
  onChange: (date: string) => void
  onBlur?: InputProps["onBlur"]
  disabled?: boolean
  readOnly?: boolean
  middleware?: Middleware[]
}

type CustomInputProps = {
  value?: string
  onClick?: () => void
}

export default function DatePickerComponent({
  size,
  date,
  minDate,
  maxDate,
  isInvalid,
  openToDate,
  errorMessage,
  placeholder,
  description,
  inputRef,
  isClearable,
  offset,
  onChange,
  onBlur,
  disabled,
  readOnly,
  middleware: middlewareProps,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)

  const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>((props, ref) => {
    const { value } = props

    return (
      <div className="relative">
        <Input
          ref={ref}
          value={value}
          size={size}
          isReadOnly
          description={description}
          labelPlacement="outside"
          variant="bordered"
          placeholder={placeholder || "YYYY.MM.DD"}
          className="bolta-input"
          isInvalid={isInvalid}
          isClearable={isClearable}
          errorMessage={errorMessage}
          disabled={disabled}
          isDisabled={disabled}
          readOnly={readOnly}
          startContent={
            <CalendarIcon className="mt-1 h-6 w-[26px] text-default-400" />
          }
          onBlur={onBlur}
          {...(isClearable ? { onClear: () => onChange("") } : {})}
        />
        <div
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
          onClick={() => setIsOpen(true)}
        ></div>
      </div>
    )
  })
  CustomInput.displayName = "CustomInput"

  const handleChange = (date: Date | null) => {
    if (!date) return

    if (disabled || readOnly) {
      return
    }

    onChange(dayjs(date).format("YYYY-MM-DD"))
    setIsOpen(false)
  }

  const middleware = React.useMemo(() => {
    const [crossAxis, mainAxis] = offset || [0, 0]
    return middlewareProps
      ? middlewareProps
      : [
          flip(),
          offsetMiddleware({
            crossAxis,
            mainAxis,
          }),
          shiftMiddleware({
            mainAxis: true,
            crossAxis: true,
          }),
        ]
  }, [offset, middlewareProps])

  const popperModifiers = React.useMemo(() => {
    return [
      {
        name: "offset",
        options: {
          offset: offset || [0, 0],
        },
        fn: state => state,
      },
    ]
  }, [offset])

  return (
    <div>
      {isOpen && (
        <div
          className="fixed left-0 top-0 z-10 h-[100dvh] w-screen"
          onClick={() => setIsOpen(false)}
        />
      )}

      <DatePicker
        open={isOpen}
        selected={date ? new Date(date) : undefined}
        locale={ko}
        minDate={minDate ? new Date(minDate) : undefined}
        maxDate={maxDate ? new Date(maxDate) : undefined}
        dateFormat="yyyy.MM.dd"
        onChange={date => handleChange(date)}
        customInput={<CustomInput ref={inputRef} />}
        openToDate={openToDate}
        disabled={disabled}
        readOnly={readOnly}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => (
          <DatePickerHeader
            date={date}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
            changeMonth={changeMonth}
            changeYear={changeYear}
            prevMonthButtonDisabled={prevMonthButtonDisabled}
            nextMonthButtonDisabled={nextMonthButtonDisabled}
          />
        )}
        popperProps={{
          /**
           * @see https://github.com/Hacker0x01/react-datepicker/issues/4564
           */
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          middleware: middleware ? middleware : undefined,
        }}
        popperModifiers={middleware ? undefined : popperModifiers}
        showPopperArrow={false}
      />
    </div>
  )
}
